import {$qs, $qsAll} from '../../@shared/ts/dom';
import {fieldGetSet} from './checkout';
import {Feature} from './features';
import {FeatureFlag} from '../../@type/features';
import {Loader} from '@googlemaps/js-api-loader';
import {getFormattedStreetAddress} from '../../@shared/ts/address';

let shippingAutocomplete: google.maps.places.Autocomplete | undefined;
let billingAutocomplete: google.maps.places.Autocomplete | undefined;

function shouldEnableAddressAutocomplete() {
	if (Feature.enabled(FeatureFlag.ADDRESS_AUTOCOMPLETE)) {
		const activeLocations = Feature.metadata(FeatureFlag.ADDRESS_AUTOCOMPLETE, 'active_locations');

		if (activeLocations === 'default' || activeLocations === 'checkout_page_only') {
			return true;
		}
	}

	return false;
}

export function initAddressAutocomplete() {
	if (!shouldEnableAddressAutocomplete()) {
		return;
	}

	const loader = new Loader({
		apiKey: 'AIzaSyAL_j0UvKJacQhZ3OedCj_bkcTbuO63MVU',
		version: 'weekly',
		libraries: ['places'],
	});

	loader.importLibrary('places').then(async () => {
		const shippingInput = $qs<HTMLInputElement>('form [name="shipping_address_1"]');
		const newBillingInput = $qs<HTMLInputElement>('form [name="billing_address_1"]');

		if (shippingInput) {
			const shippingOptions = {
				/**
				 * Currently using both address_components and formatted_address to cover the most street addresses with least conditional logic
				 * Including more fields costs more, so if enough country specific logic is written to allow the removal of formatted_address, it should be removed
				 */
				fields: ['address_components', 'formatted_address'],
				types: ['address'],
			};

			shippingAutocomplete = new google.maps.places.Autocomplete(shippingInput, shippingOptions);
			shippingAutocomplete?.addListener('place_changed', () => {
				fillInAddress('shipping');
			});
		}

		if (newBillingInput) {
			const billingOptions = {
				/**
				 * Currently using both address_components and formatted_address to cover the most street addresses with least conditional logic
				 * Including more fields costs more, so if enough country specific logic is written to allow the removal of formatted_address, it should be removed
				 */
				fields: ['address_components', 'formatted_address'],
				types: ['address'],
			};

			billingAutocomplete = new google.maps.places.Autocomplete(newBillingInput, billingOptions);
			billingAutocomplete?.addListener('place_changed', () => {
				fillInAddress('billing');
			});
		}

		// Prevent submitting the form when ENTER key is pressed on the input.
		$qsAll('#pp-info-form', $form => {
			$form.addEventListener('keypress', preventSubmitListener);
		});
	}).catch(error => {
		console.log('An error occurred during Google Maps Places API importLibrary:', error);
	});
}

function preventSubmitListener(event: Event) {
	const key = (event as KeyboardEvent).key || 0;
	if (key === 'Enter') {
		event.preventDefault();
	}
}

/**
 * Fills in address fields as best possible
 * Implemented mostly to handle US and common European countries. Will need to be tweaked for new countries as merchants ask.
 * Known issues (not resolved due to extreme scope)
 * - For many countries, state/province does not correctly fill due to either inconsistent shortcodes between Google & WooCommerce or different address component name (other than administrative_area_1)
 */
// eslint-disable-next-line complexity
function fillInAddress(section: string) {
	const address1Field = fieldGetSet(`${section}_address_1`);
	const address2Field = fieldGetSet(`${section}_address_2`);
	const postalField = fieldGetSet(`${section}_postcode`);
	const cityField = fieldGetSet(`${section}_city`);
	const stateField = fieldGetSet(`${section}_state`);
	const provinceField = fieldGetSet(`${section}_province`);

	// Clear all fields, to make it clear to the user what wasn't updated
	if (address1Field) {
		address1Field.set('');
	}

	if (address2Field) {
		address2Field.set('');
	}

	if (postalField) {
		postalField.set('');
	}

	if (cityField) {
		cityField.set('');
	}

	if (stateField) {
		stateField.set('');
	}

	if (provinceField) {
		provinceField.set('');
	}

	let place: google.maps.places.PlaceResult | undefined;
	// Get the place details from the autocomplete object.
	if (section === 'billing') {
		place = billingAutocomplete?.getPlace();
	}

	if (section === 'shipping') {
		place = shippingAutocomplete?.getPlace();
	}

	if (!place) {
		return;
	}

	// Set country field first so province/state field can update accordingly.
	const country = place.address_components?.find((component: google.maps.GeocoderAddressComponent) => component.types[0] === 'country');
	const countryField = fieldGetSet(`${section}_country`);
	if (countryField && country) {
		countryField.set(country.short_name);
		jQuery(countryField).trigger('change');
	}

	let postcode = '';

	if (!place.address_components) {
		return;
	}

	// Get each component of the address from the place details,
	// and then fill-in the corresponding field on the form.
	// place.address_components are google.maps.GeocoderAddressComponent objects
	// which are documented at http://goo.gle/3l5i5Mr
	for (const component of place.address_components) {
		const componentType = component.types[0];

		switch (componentType) {
			case 'postal_code': {
				postcode = `${component.long_name}${postcode}`;
				break;
			}

			case 'locality':
			case 'postal_town': {
				if (cityField) {
					cityField.set(component.long_name);
				}

				break;
			}

			// Only autofill state/province value if it isn't hidden by wc rule and if the autofill data is valid.

			case 'administrative_area_level_1': {
				// Fill in both fields, since both will likely be found by the querySelector
				if (stateField) {
					stateField.set(component.short_name);
					jQuery(stateField).trigger('change');
				}

				if (provinceField) {
					provinceField.set(component.short_name);
					jQuery(provinceField).trigger('change');
				}

				break;
			}

			case 'country':
				// Should have been set before for loop
				break;
			default:
		}
	}

	if (address1Field) {
		address1Field.set(getFormattedStreetAddress(place.formatted_address!));
	}

	if (postalField) {
		postalField.set(postcode);
	}

	// After filling the form with address components from the Autocomplete
	// prediction, set cursor focus on the second address line to encourage
	// entry of subpremise information such as apartment, unit, or floor number.
	const address2Element = $qs<HTMLInputElement>(`[name="${section}_address_2"]:not(.hide)`);
	if (address2Element) {
		address2Element.focus();
	}
}
